import { gql } from 'graphql-request'
import { gqlMutate, gqlQuery } from '@hapana/shared/web/services/requests.service'

import type {
  EquipmentLampLog as EquipmentLampLogRecord,
  EquipmentLampSetStatus as EquipmentLampSetStatusRecord,
  EquipmentLampSiteStatus as EquipmentLampSiteStatusRecord,
  EquipmentLampStatus as EquipmentLampStatusRecord,
} from '@hapana/shared/schemas/equipment/equipment-schemas'
import type {
  AdjustEquipmentLampHoursInput,
  EquipmentLampLogPage as EquipmentLampLogPageRecord,
  LogEquipmentLampChangeInput,
  UpdateEquipmentLampSettingsInput,
} from '@hapana/shared/schemas/equipment/equipment-lamp-input-schemas'

export type {
  EquipmentLampLogEventType,
  EquipmentLampSet,
  EquipmentLampState,
} from '@hapana/shared/schemas/equipment/equipment-schemas'
export type { LampSetSettingsInput } from '@hapana/shared/schemas/equipment/equipment-lamp-input-schemas'
export type {
  AdjustEquipmentLampHoursInput,
  LogEquipmentLampChangeInput,
  UpdateEquipmentLampSettingsInput,
}

// GraphQL carries every Date scalar as an epoch-millisecond string.
type Serialized<T> = T extends Date
  ? string
  : T extends Array<infer Item>
    ? Array<Serialized<Item>>
    : T extends object
      ? { [Key in keyof T]: Serialized<T[Key]> }
      : T

const LAMP_SET_STATUS_FIELD_NAMES = [
  'lampLifeHours',
  'lampWarningHours',
  'currentMinutes',
  'state',
  'baselineAt',
  'lastLampChangeAt',
  'uncountedCodedSessions',
] as const satisfies ReadonlyArray<keyof EquipmentLampSetStatusRecord>

const LAMP_LOG_FIELD_NAMES = [
  'equipmentLampLogId',
  'lampSet',
  'eventType',
  'effectiveAt',
  'previousMinutes',
  'newMinutes',
  'deltaMinutes',
  'notes',
  'createdByName',
  'createdAt',
] as const satisfies ReadonlyArray<keyof EquipmentLampLogRecord>

export type EquipmentLampSetStatus = Serialized<
  Pick<EquipmentLampSetStatusRecord, (typeof LAMP_SET_STATUS_FIELD_NAMES)[number]>
>

export type EquipmentLampStatus = Omit<Serialized<EquipmentLampStatusRecord>, 'body' | 'facial'> & {
  body?: EquipmentLampSetStatus | null
  facial?: EquipmentLampSetStatus | null
}

export type EquipmentLampSiteStatus = Omit<
  Serialized<EquipmentLampSiteStatusRecord>,
  'equipment'
> & {
  equipment: EquipmentLampStatus[]
}

export type EquipmentLampLog = Serialized<
  Pick<EquipmentLampLogRecord, (typeof LAMP_LOG_FIELD_NAMES)[number]>
>

export type EquipmentLampLogPage = Omit<Serialized<EquipmentLampLogPageRecord>, 'items'> & {
  items: EquipmentLampLog[]
}

const LAMP_SET_STATUS_FIELDS = LAMP_SET_STATUS_FIELD_NAMES.join('\n')

const LAMP_LOG_FIELDS = LAMP_LOG_FIELD_NAMES.join('\n')

const equipmentLampStatusesQuery = gql`
  query EquipmentLampStatuses($siteId: ID!) {
    equipmentLampStatuses(siteId: $siteId) {
      timezone
      siteToday
      equipment {
        equipmentId
        state
        body { ${LAMP_SET_STATUS_FIELDS} }
        facial { ${LAMP_SET_STATUS_FIELDS} }
      }
    }
  }
`

const equipmentLampLogsQuery = gql`
  query EquipmentLampLogs(
    $equipmentId: ID!
    $lampSet: EquipmentLampSet
    $limit: Int
    $cursor: String
  ) {
    equipmentLampLogs(
      input: { equipmentId: $equipmentId, lampSet: $lampSet, limit: $limit, cursor: $cursor }
    ) {
      items { ${LAMP_LOG_FIELDS} }
      nextCursor
    }
  }
`

const updateEquipmentLampSettingsMutation = gql`
  mutation UpdateEquipmentLampSettings($input: UpdateEquipmentLampSettingsInput!) {
    updateEquipmentLampSettings(input: $input) {
      equipmentId
      lampTrackingEnabled
      lampLifeHours
      lampWarningHours
      facialLampTrackingEnabled
      facialLampLifeHours
      facialLampWarningHours
    }
  }
`

const logEquipmentLampChangeMutation = gql`
  mutation LogEquipmentLampChange($input: LogEquipmentLampChangeInput!) {
    logEquipmentLampChange(input: $input) { ${LAMP_LOG_FIELDS} }
  }
`

const adjustEquipmentLampHoursMutation = gql`
  mutation AdjustEquipmentLampHours($input: AdjustEquipmentLampHoursInput!) {
    adjustEquipmentLampHours(input: $input) { ${LAMP_LOG_FIELDS} }
  }
`

export const getEquipmentLampStatuses = gqlQuery<{
  equipmentLampStatuses: EquipmentLampSiteStatus
}>({
  key: 'EQUIPMENT_LAMP_STATUSES',
  query: equipmentLampStatusesQuery,
})

export const getEquipmentLampLogs = gqlQuery<{ equipmentLampLogs: EquipmentLampLogPage }>({
  key: 'EQUIPMENT_LAMP_LOGS',
  query: equipmentLampLogsQuery,
})

export const updateEquipmentLampSettings = (input: UpdateEquipmentLampSettingsInput) =>
  gqlMutate({ mutation: updateEquipmentLampSettingsMutation, variables: { input } })

export const logEquipmentLampChange = (input: LogEquipmentLampChangeInput) =>
  gqlMutate({ mutation: logEquipmentLampChangeMutation, variables: { input } })

export const adjustEquipmentLampHours = (input: AdjustEquipmentLampHoursInput) =>
  gqlMutate({ mutation: adjustEquipmentLampHoursMutation, variables: { input } })
